
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/ui/dialog';
import { Button } from '@shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/ui/select';
import { Label } from '@shared/ui/label';
import { Checkbox } from '@shared/ui/checkbox';
import { DownloadIcon } from 'lucide-react';
import { logEvent } from '@shared/analytics/analytics';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose }) => {
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedParent, setSelectedParent] = useState('');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [includeAssociations, setIncludeAssociations] = useState(true);

  const entityTypes = [
    { value: 'region', label: 'Region' },
    { value: 'country', label: 'Country' },
    { value: 'state', label: 'State' },
    { value: 'city', label: 'City/County' },
    { value: 'zone', label: 'Zone' },
    { value: 'division', label: 'Division' },
    { value: 'areas', label: 'Areas' },
    { value: 'sub-areas', label: 'Sub-Areas' },
    { value: 'premises', label: 'Premises' }
  ];

  const handleExport = () => {
    // Generate CSV content based on selections
    const headers = [
      'Entity Type', 'ID', 'Name', 'Parent Type', 'Parent ID', 'Parent Name',
      'Description', 'Manager', 'Status', 'Created Date', 'Updated Date'
    ];
    
    // Add specific fields based on entity type
    if (selectedEntity === 'premises') {
      headers.push('Address', 'Premise Type', 'Floors', 'Units', 'Total Area', 'Occupancy');
    }
    
    // Mock data generation
    const mockData = [
      ['Region', 'REG001', 'North Region', '', '', '', 'Northern service area', 'John Doe', 'Active', '2024-01-01', '2024-06-01'],
      ['State', 'ST001', 'Delhi', 'Region', 'REG001', 'North Region', 'Delhi state', 'Jane Smith', 'Active', '2024-01-02', '2024-06-02'],
      ['City', 'CT001', 'New Delhi', 'State', 'ST001', 'Delhi', 'Capital city', 'Bob Johnson', 'Active', '2024-01-03', '2024-06-03']
    ];
    
    const csvContent = [headers, ...mockData].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedEntity || 'all'}_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    logEvent(`${selectedEntity} Exported`, {
      selectedEntity,
      selectedParent,
    })
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export Data</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="entityType">Entity Type to Export</Label>
              <Select value={selectedEntity} onValueChange={setSelectedEntity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedEntity && selectedEntity !== 'all' && (
              <div className="space-y-2">
                <Label htmlFor="parentEntity">Specific Parent (Optional)</Label>
                <Select value={selectedParent} onValueChange={setSelectedParent}>
                  <SelectTrigger>
                    <SelectValue placeholder="All parents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent1">Sample Parent 1</SelectItem>
                    <SelectItem value="parent2">Sample Parent 2</SelectItem>
                    <SelectItem value="parent3">Sample Parent 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-3">
              <Label>Export Options</Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeChildren"
                  checked={includeChildren}
                  onCheckedChange={(checked) => setIncludeChildren(checked as boolean)}
                />
                <Label htmlFor="includeChildren" className="text-sm">
                  Include all child entities and grandchildren
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeAssociations"
                  checked={includeAssociations}
                  onCheckedChange={(checked) => setIncludeAssociations(checked as boolean)}
                />
                <Label htmlFor="includeAssociations" className="text-sm">
                  Include parent-child associations
                </Label>
              </div>
            </div>

            {selectedEntity && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p className="font-medium mb-1">Export will include:</p>
                <ul className="text-gray-600 space-y-1">
                  <li>• {selectedEntity === 'all' ? 'All entity types' : `${selectedEntity} entities`}</li>
                  {includeChildren && <li>• All child and grandchild entities</li>}
                  {includeAssociations && <li>• Parent-child relationship data</li>}
                  <li>• Entity metadata and basic information</li>
                </ul>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={!selectedEntity}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <DownloadIcon className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
